import { Link } from 'react-router-dom';
import { useWall, WallButtons } from './credit-wall';
import { ExportButtons } from './export';
import { fmt, pct, usd } from '../lib/format';
import { Table, useApi } from './kit';

// What the credits buy and how long they last, on Billing and the campaign page: the forecast with its warnings (no
// silent stop when credits run out), the campaign's results counted in meetings and interested replies with their
// cost, and its leads with why any was not sent (only verified addresses are).

export type Forecast = {
  available: number; granted: number; used_share: number | null; cycle_end: string; renews: boolean;
  pace_per_day: number; daily_budget: number | null; runs_out_at: string | null; runs_out_before_renewal: boolean;
  state: 'ok' | 'out_of_credits' | 'daily_budget_reached'; subject: 'campaign' | 'workspace';
  warnings: Array<{ kind: 'used_80' | 'runs_out_before_renewal' | 'out_of_credits'; text: string }>; text: string;
};

/** The forecast line, and a banner for each warning: out of credits says autopilot paused, with Top up and Change plan. */
export function CreditForecast({ campaignId }: { campaignId?: string }) {
  // the buttons come from the server (who may pay, what each costs): Billing's own sections, the free-credits card, or a notice to the owner
  const wall = useWall(null);
  const f = useApi<Forecast>(`/v1/usage/forecast${campaignId ? `?campaign_id=${campaignId}` : ''}`).data;
  if (!f) return null;
  const out = f.warnings.find((w) => w.kind === 'out_of_credits');
  const others = f.warnings.filter((w) => w.kind !== 'out_of_credits');
  return (
    <div className="credit-forecast">
      {out ? (
        <div className="notice notice-bad" role="alert">
          <strong>Autopilot paused: out of credits.</strong> {out.text}
          {wall.data ? <WallButtons actions={wall.data.buttons} what="Autopilot is paused" needed={null} /> : null}
          {wall.data && !wall.data.is_owner ? <span className="small muted" style={{ display: 'block', marginTop: 6 }}>{wall.data.message}</span> : null}
        </div>
      ) : null}
      {others.map((w) => (
        <div key={w.kind} className="notice notice-warn" role="status">
          {w.text}{' '}{w.kind === 'used_80' ? <Link to="/billing">See usage</Link> : wall.data ? <WallButtons actions={wall.data.buttons} what={w.text} needed={null} /> : null}
        </div>
      ))}
      {/* the run-out warning already says the forecast */}
      {!out && !others.some((w) => w.kind === 'runs_out_before_renewal') ? <p className="muted small">{f.text}</p> : null}
    </div>
  );
}

export type CampaignStats = {
  leads: number; sent: number; replied: number; hard_bounced: number; hard_bounce_rate: number | null; skipped?: number;
  meetings?: number; interested_replies?: number; credits_spent?: number; usd_spent_est?: number; usd_per_credit?: number;
  cost_per_meeting_usd?: number | null; cost_per_interested_reply_usd?: number | null; cost_definition?: string;
};

/** The campaign's numbers: hard bounces prominent (domain protection pauses over 3%), meetings and what they cost. */
export function CampaignResults({ s }: { s: CampaignStats }) {
  const high = s.hard_bounce_rate !== null && s.hard_bounce_rate > 0.03;
  const tiles: Array<{ label: string; value: string; hint?: string; warn?: boolean }> = [
    { label: 'Leads', value: fmt(s.leads), hint: s.skipped ? `${s.skipped} skipped` : undefined },
    { label: 'Sent', value: fmt(s.sent) },
    { label: 'Replied', value: fmt(s.replied) },
    { label: 'Hard bounces', value: pct(s.hard_bounce_rate), hint: `${fmt(s.hard_bounced)} of ${fmt(s.sent)} · paused over 3%`, warn: high },
    { label: 'Meetings booked', value: fmt(s.meetings ?? 0) },
    { label: 'Interested replies', value: fmt(s.interested_replies ?? 0) },
    { label: 'Cost per meeting', value: usd(s.cost_per_meeting_usd ?? null), hint: s.cost_per_interested_reply_usd != null ? `${usd(s.cost_per_interested_reply_usd)} per interested reply` : 'per interested reply: —' },
    { label: 'Credits spent', value: fmt(s.credits_spent ?? 0), hint: `≈ ${usd(s.usd_spent_est ?? 0)}` },
  ];
  return (
    <>
      <div className="tiles small">
        {tiles.map((t) => (
          <div key={t.label} className={`tile${t.warn ? ' attn' : ''}`}>
            <span className={`tile-value${t.warn ? ' warn' : ''}`}>{t.value}</span><span className="tile-label">{t.label}</span>{t.hint ? <span className="tile-hint">{t.hint}</span> : null}
          </div>
        ))}
      </div>
      {s.cost_definition ? <p className="muted small">{s.cost_definition}</p> : null}
    </>
  );
}

type Lead = { id: string; first_name: string | null; last_name: string | null; title: string | null; domain: string; state: string; verification_status: string; skipped_reason: string | null };
const VERIFIED: Record<string, string> = { valid: 'Verified', risky: 'Catch-all (not sent)', invalid: 'Bad address', unknown: 'Could not verify', unverified: 'Not verified yet' };

/** The campaign's leads, newest first: who, where they stand, the address check, and why a lead was skipped. */
export function CampaignLeads({ campaignId }: { campaignId: string }) {
  const q = useApi<{ data: Lead[] }>(`/v1/campaigns/${campaignId}/leads?limit=50`);
  const rows = q.data?.data ?? [];
  if (!rows.length) return null;
  return (
    <section className="section" aria-label="Leads">
      <div className="section-head"><h2>Leads</h2><ExportButtons base={`/v1/campaigns/${campaignId}/leads`} what="leads" /></div>
      <p className="muted small">Only addresses verified valid in the last 30 days are sent; catch-all, invalid and unverified ones are skipped with the reason.</p>
      <Table caption="Leads in this campaign" rows={rows} columns={[
        { key: 'name', label: 'Person', render: (r) => [r.first_name, r.last_name].filter(Boolean).join(' ') || '—' },
        { key: 'domain', label: 'Company' },
        { key: 'state', label: 'State' },
        { key: 'verification_status', label: 'Email', render: (r) => VERIFIED[r.verification_status] ?? r.verification_status },
        { key: 'skipped_reason', label: 'Skipped because', render: (r) => r.skipped_reason ?? '—' },
      ]} />
    </section>
  );
}

// Research depth (src/research/depth.ts): researched (the default) or volume, and what one contact costs at it.
export const DEPTH_LABEL: Record<string, string> = { researched: 'Researched', volume: 'Volume' };
const DEPTH_NOTE: Record<string, string> = {
  researched: 'Each account is read on up to 4 of its pages before it counts as a fit.',
  volume: 'A light check: what the record says and the homepage only. Emails carry fewer specifics; contacts are verified the same.',
};

/** The campaign's depth with the credits (and an estimate in dollars) one contact costs. */
export function CampaignDepth({ depth, perContact, usdPerCredit, onChange }: {
  depth: string; perContact?: { credits: number }; usdPerCredit?: number; onChange?: () => void;
}) {
  return (
    <p className="muted small campaign-depth">
      <span className={`chip${depth === 'volume' ? ' next' : ''}`}>{DEPTH_LABEL[depth] ?? depth}</span>{' '}
      {perContact ? <>{fmt(perContact.credits)} credits a contact{usdPerCredit ? ` (≈ ${usd(perContact.credits * usdPerCredit)})` : ''}. </> : null}
      {DEPTH_NOTE[depth] ?? ''}
      {onChange ? <>{' '}<button type="button" className="link" onClick={onChange}>Change</button></> : null}
    </p>
  );
}

type DepthRow = { depth: string; campaigns: number; leads: number; credits_per_contact: number; meetings: number; interested_replies: number; credits_spent: number; usd_spent_est: number; cost_per_meeting_usd: number | null; cost_per_interested_reply_usd: number | null };

/** Cost per meeting of researched campaigns next to volume ones, so the two can be compared (estimates). */
export function DepthCompare() {
  const rows = (useApi<{ data: DepthRow[] }>('/v1/metrics/costs-by-depth').data?.data ?? []).filter((r) => r.campaigns > 0);
  if (rows.length < 2) return null;
  return (
    <Table caption="Researched and volume campaigns" rows={rows.map((r) => ({ ...r, id: r.depth }))} columns={[
      { key: 'depth', label: 'Depth', render: (r) => DEPTH_LABEL[r.depth] ?? r.depth },
      { key: 'credits_per_contact', label: 'Credits a contact' }, { key: 'leads', label: 'Leads', render: (r) => fmt(r.leads) },
      { key: 'meetings', label: 'Meetings', render: (r) => fmt(r.meetings) }, { key: 'interested_replies', label: 'Interested', render: (r) => fmt(r.interested_replies) },
      { key: 'credits_spent', label: 'Credits spent', render: (r) => `${fmt(r.credits_spent)} (≈ ${usd(r.usd_spent_est)})` },
      { key: 'cost_per_meeting_usd', label: 'Cost per meeting', render: (r) => usd(r.cost_per_meeting_usd) },
    ]} />
  );
}
